import { arLayerModelOf, arLayerOf, arSceneIdOf } from "./arKeys.ts";
import { normalizeArInteract } from "./arInteract.ts";

/**
 * 🩺 **마법 장면 하나하나가 아이에게 어떻게 닿고 있나** — 관리자 [🪄 마법 점검] 과 `pnpm check:magic` 이 같이 쓴다.
 *
 * ## 왜 있나 (2026-09-13)
 * 「게시됐나 · 그림이 그림으로 오나 · 카메라가 켜졌나 · 못 알아보는 쪽인데 카메라가 켜져 있나」는
 * 지금까지 **명령줄 검사만** 알았다. 운영자는 「25쪽 카메라 끄기를 눌렀는데 됐는지 모르겠다」를 겪었고,
 * 게시했는지·빠진 게 있는지 알려면 개발자에게 물어야 했다. 이 저장소가 배운 대로 — 명령줄뿐이면 아무도 안 본다.
 *
 * ## 🔴 판정은 여기 한 곳
 * 무엇을 「게시됨」으로 볼지는 `magicPublishPlan` 과 **같은 비교**(정의를 정규화해 견준다).
 * 화면마다 다시 짜면 「출시 준비에선 다 올라갔다는데 점검에선 게시 전」이 된다.
 *
 * ## 네트워크는 여기서 안 한다
 * 그림이 정말 그림으로 오는지는 화면(또는 검사)이 물어보고, 그 **답을 읽는 규칙**만 여기 둔다(`servedState`).
 */

export interface HealthEntry {
  url?: string;
  interact?: unknown;
  /** 저장소 표시 — 카메라가 못 알아보는 쪽(yaml `camera: false`). 게시본에는 없다. */
  camera?: boolean;
  [k: string]: unknown;
}
export type HealthAssets = Record<string, HealthEntry>;

/** 아이 화면에 이 장면이 어떻게 나가 있나. */
export type PublishState =
  /** 게시본의 정의가 새로 다듬은 판과 같다 */
  | "live"
  /** 게시본에도 있지만 새로 다듬은 판과 다르다(관리자에서 고쳤을 수 있다) */
  | "differs"
  /** 게시본에 정의가 없다 — 눌러도 아무 일이 안 난다 */
  | "missing";

/** 카메라로 비추면 어떻게 되나. */
export type CameraState =
  /** 인식 파일이 있다 */
  | "on"
  /** 인식 파일이 없다 — 책의 「✨ 마법 보기」만 된다 */
  | "off"
  /** 카메라가 못 알아보는 쪽이라 일부러 꺼 뒀다 — 맞는 상태 */
  | "unrecognizable"
  /** 🔴 카메라가 못 알아보는 쪽인데 인식 파일이 **켜져 있다** — 아이가 카메라를 켜고 헛되이 비춘다 */
  | "blocked";

export interface HealthImage {
  layerId: string;
  /** 아이가 받는 주소 — 게시본이 있으면 게시본, 없으면 새로 다듬은 판 */
  url: string;
}

export interface MagicHealthRow {
  sceneId: string;
  /** 🃏 책 쪽(`poljjak.p27`)인가 경주 카드(`ar_sumaksae`)인가 — 사람 말로 부르는 법과 고치러 가는 화면이 다르다 */
  kind: "page" | "card";
  /** 책 id(`poljjak`) — 쪽 번호를 찾아 사람 말로 보여 주려고. 카드는 빈 글자 */
  bookId: string;
  /** 쪽 id(`p27`). 카드는 장면 id 그대로 */
  pageId: string;
  publish: PublishState;
  camera: CameraState;
  layers: number;
  hotspots: number;
  images: HealthImage[];
  /** 돌려 보는 3D 파일이 있는 층 */
  models: HealthImage[];
}

export interface MagicHealth {
  rows: MagicHealthRow[];
  /** 운영자가 **지금 손써야 할** 장면 수(게시 전·다름·카메라 막힘). 그림 도착 여부는 화면이 물어본 뒤 더한다. */
  needsAction: number;
}

/**
 * 새로 다듬은 판(저장소 정적 매니페스트)과 게시본을 견주어 장면마다 상태를 낸다.
 *
 * @param repo `content/asset-manifest.json` — 정의·`camera: false` 표시가 여기 있다
 * @param live `/api/asset-manifest` — 아이가 지금 받는 것
 */
export function magicHealth(repo: HealthAssets, live: HealthAssets): MagicHealth {
  const rows: MagicHealthRow[] = [];
  for (const key of Object.keys(repo)) {
    if (!key.startsWith("ar.target.")) continue;
    const def = normalizeArInteract(repo[key]?.interact);
    if (!def) continue;
    const sceneId = arSceneIdOf(key);
    if (!sceneId) continue;
    /**
     * 🃏 **경주 카드도 든다**(2026-09-13 · 수막새 카드에 3D). 예전엔 「책 쪽만」이라 카드에 마법을 붙이면
     * 점검 화면에 **아예 안 나왔다** — 게시했는지·카메라가 켜졌는지 운영자가 볼 길이 없었다.
     */
    const dot = sceneId.indexOf(".");
    const kind: MagicHealthRow["kind"] = dot > 0 ? "page" : "card";

    const liveDef = normalizeArInteract(live[key]?.interact);
    const publish: PublishState =
      liveDef === null ? "missing" : JSON.stringify(liveDef) === JSON.stringify(def) ? "live" : "differs";

    // 카메라 — 아이가 받는 주소는 게시본이 있으면 게시본 것(항목 통째로 이긴다)
    const camUrl = live[key] !== undefined ? live[key]?.url : repo[key]?.url;
    const cameraOff = repo[key]?.camera === false;
    const camera: CameraState = cameraOff ? (camUrl ? "blocked" : "unrecognizable") : camUrl ? "on" : "off";

    const images: HealthImage[] = [];
    const models: HealthImage[] = [];
    const keys = new Set([...Object.keys(repo), ...Object.keys(live)]);
    for (const k of keys) {
      const img = arLayerOf(k);
      const mdl = img ? null : arLayerModelOf(k);
      const who = img ?? mdl;
      if (!who || who.sceneId !== sceneId) continue;
      const url = (live[k] ?? repo[k])?.url;
      if (typeof url !== "string" || !url) continue;
      (img ? images : models).push({ layerId: who.layerId, url });
    }
    images.sort((a, b) => a.layerId.localeCompare(b.layerId));
    models.sort((a, b) => a.layerId.localeCompare(b.layerId));

    rows.push({
      sceneId,
      kind,
      bookId: kind === "page" ? sceneId.slice(0, dot) : "",
      pageId: kind === "page" ? sceneId.slice(dot + 1) : sceneId,
      publish,
      camera,
      layers: def.layers.length,
      hotspots: def.hotspots.length,
      images,
      models,
    });
  }
  rows.sort((a, b) => a.sceneId.localeCompare(b.sceneId));
  const needsAction = rows.filter((r) => r.publish !== "live" || r.camera === "blocked").length;
  return { rows, needsAction };
}

/** 그림이 아이에게 어떻게 오나 — 할 일이 달라서 셋으로 가른다. */
export type ServedState =
  /** 제대로 그림으로 온다 */
  | "ok"
  /** 원본에도 없다 → **배포**해야 한다 */
  | "not_deployed"
  /** 원본엔 있는데 그 주소의 캐시에 문서가 박혔다 → 배포로는 안 낫고 시간이 푼다 */
  | "rotten"
  /** 못 물어봤다 — 「없다」가 아니다 */
  | "unknown";

/**
 * 두 번 물어본 답 → 상태. `pnpm check:magic` 의 `servedAsImage` 와 **같은 규칙**.
 *
 * 🔴 묻는 순서: ① 다른 열쇠(`?cb=`)로 원본을 먼저 — 캐시를 안 만든다 ② 원본에 있을 때만 진짜 주소.
 * 거꾸로 물으면 배포 전에 폴백 HTML 이 그 주소의 답으로 4시간 박힌다(2026-09-10 실제로 그랬다).
 *
 * @param originType `?cb=` 로 물은 답의 content-type. 못 물어봤으면 `null`
 * @param realType 진짜 주소로 물은 답의 content-type. 안 물었거나 못 물어봤으면 `null`
 * @param accept 제대로 온 것으로 칠 형식 — 층 그림은 `image/`, 3D 파일은 `model/`·`application/octet-stream`.
 *   🔴 3D 에 `image/` 를 기대하면 멀쩡한 `.glb` 가 전부 「안 내려갔다」가 된다.
 */
export function servedState(
  originType: string | null,
  realType: string | null,
  accept: readonly string[] = ["image/"],
): ServedState {
  const good = (t: string) => accept.some((a) => t.startsWith(a));
  if (originType === null) return "unknown";
  if (!good(originType)) return "not_deployed";
  if (realType === null) return "unknown";
  return good(realType) ? "ok" : "rotten";
}

/** 3D 파일(.glb)이 제대로 왔다고 칠 형식. */
export const MODEL_ACCEPT: readonly string[] = ["model/", "application/octet-stream"];

/** 교실 회선(1.5Mbps)에서 1초에 받는 양(KB) — 무게를 「몇 초」로 말할 때는 이 한 곳에서. */
export const CLASS_KB_PER_SEC = 190;
/** 이만큼(KB)을 교실 회선에서 받는 데 몇 초. */
export function classLoadSeconds(kb: number): number {
  return kb / CLASS_KB_PER_SEC;
}
/**
 * 🧊 **3D 파일 무게 문턱** (2026-09-14) — 올린 원본이 **그대로 아이 폰에 간다**(줄여 주는 단계가 없다).
 * 넘으면 올릴 때 경고(교실 회선 약 27초) · 더 넘으면 안 받는다. 프로젝트 폴더의 수막새 원본은 38MB 였다.
 */
export const MODEL_WARN_KB = 5 * 1024;
export const MODEL_MAX_KB = 20 * 1024;
/** 🪶 이보다 무거운 3D 는 올리기 전에 「가볍게 줄여서 올릴까요」를 묻는다(교실 회선 약 5초). 운영 수막새는 줄여서 302KB. */
export const MODEL_SLIM_KB = 1024;
/**
 * 🪶 **줄일 때 그림 크기·품질** — 관리자 브라우저(`apps/admin/src/lib/slimGlb.ts`)와 제작 스크립트(`scripts/slim-glb.py` 기본값)가
 * 같은 값을 쓴다. 바탕색은 또렷하게(1024), 거칠기·법선·가림은 작게(512) — 손바닥만 한 물건을 책 폭 0.62 로 보인다.
 */
export const GLB_SLIM = { basePx: 1024, otherPx: 512, quality: 0.72, normalQuality: 0.78 } as const;

/** 이보다 무거운 층 그림은 교실 회선(1.5Mbps)에서 몇 초씩 빈 칸 — `check:magic` 과 같은 문턱. */
export const MAGIC_HEAVY_KB = 400;

/** `Content-Range: bytes 0-0/12345` → 전체 KB. 못 읽으면 `null`(「가볍다」가 아니다). */
export function sizeKbFromRange(contentRange: string | null): number | null {
  const m = /\/(\d+)\s*$/.exec(contentRange ?? "");
  return m ? Number(m[1]) / 1024 : null;
}

/**
 * 전체 KB — 범위 요청의 답(`Content-Range`)이 있으면 그것, 없으면 `Content-Length`. 둘 다 못 읽으면 `null`.
 *
 * 🔴 **관리자 화면은 범위 요청을 안 한다** (2026-09-13 운영 실측). `Range` 머리는 브라우저에 따라 사전 확인
 * (preflight)을 부르는데, 운영 서버는 그 확인에 `/art/` 405 · `/assets/` 404 로 답한다 — 그러면 그림이 **전부**
 * 「못 물어봤어요」가 된다. 검사는 fetch 를 흉내 내니 초록이다(가짜 입력이 진짜와 다른 모양). 그래서 평범한 GET 의
 * 머리만 받고 끊고, 무게는 `Content-Length` 에서 읽는다. 3D(.glb)는 범위 요청에도 206 을 안 줬다.
 */
export function sizeKbFromHeaders(contentRange: string | null, contentLength: string | null): number | null {
  const fromRange = sizeKbFromRange(contentRange);
  if (fromRange !== null) return fromRange;
  const n = Number(contentLength);
  return contentLength && Number.isFinite(n) && n > 0 ? n / 1024 : null;
}
